import { useState } from "react";
import { X, Loader2, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin modal to directly cancel a placement without needing a student request.
 * Releases the seat, notifies student + association + mentor.
 */
export default function AdminCancelPlacementModal({ app, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [notifyStudent, setNotifyStudent] = useState(true);
  const [busy, setBusy] = useState(false);

  const opportunity = app.opportunity?.title || app.opportunity_title || "الفرصة";
  const studentName = app.student?.name || app.student_name || "الطالب";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("سبب الإلغاء مطلوب (5 أحرف على الأقل)");
      return;
    }
    if (!window.confirm(`تأكيد إلغاء تسكين ${studentName}؟ هذا الإجراء يحرّر المقعد ويُشعر الطالب والجمعية والموجّه.`)) return;
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/admin-cancel`, {
        reason: reason.trim(),
        notify_student: notifyStudent,
      });
      toast.success("تم إلغاء التسكين مباشرة");
      onCancelled?.();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:px-4" data-testid="admin-cancel-placement-modal">
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col animate-sheet-up sm:animate-fade-in-fast"
        style={{ maxHeight: "92dvh" }}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-rose-700">إلغاء مباشر</div>
            <h2 className="mt-1 text-lg font-heading font-black text-slate-900 flex items-center gap-2">
              <Ban className="h-5 w-5 text-rose-600 shrink-0" />
              إلغاء تسكين {studentName}
            </h2>
            <div className="mt-1 text-xs text-slate-500 truncate">«{opportunity}»</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 min-h-11 min-w-11 grid place-items-center shrink-0"
            aria-label="إغلاق"
            data-testid="admin-cancel-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900 leading-6">
            هذا إلغاء فوري بصلاحية الإدارة — سيُحرَّر المقعد تلقائيًا، وتُحدَّث حالة التسكين إلى «ملغي»، ويُشعَر كل من الطالب والجمعية والموجّه.
          </div>

          <label className="block">
            <span className="text-xs font-bold text-slate-600">
              سبب الإلغاء <span className="text-rose-600">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              minLength={5}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="اذكر سبب الإلغاء ليُسجَّل في المراجعات ويظهر للجمعية والطالب (5 أحرف على الأقل)…"
              data-testid="admin-cancel-reason"
              required
            />
          </label>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notifyStudent}
              onChange={(e) => setNotifyStudent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300"
              data-testid="admin-cancel-notify-student"
            />
            <span className="text-sm text-slate-700">إرسال إشعار + بريد للطالب يوضّح سبب الإلغاء</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-xl px-4 text-sm font-bold text-slate-600 hover:bg-slate-100"
            data-testid="admin-cancel-cancel"
          >
            تراجع
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 min-h-11 rounded-xl bg-rose-600 px-5 text-sm font-bold text-white disabled:opacity-50 hover:bg-rose-700"
            data-testid="admin-cancel-submit"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "جارٍ الإلغاء…" : "إلغاء التسكين مباشرة"}
          </button>
        </div>
      </form>
    </div>
  );
}
